import React, { useState, useEffect, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useLocation, useSearchParams } from 'react-router-dom';
import { athleteAPI, trainingSessionAPI, bananaCupAPI } from '../../services/api';
import { Athlete } from '../../types/mongodb';
import { getQuickDateRange } from './dateRangeUtils';
import { loadAnalyticsFilters, saveAnalyticsFilters, getPagePathFromRoute } from '../../utils/analyticsFilterStorage';
import {
  AnalyticsContainer,
  PageHeader,
  PageTitle,
  PageDescription,
  DataSelectionSection,
  SelectionGrid,
  SelectionGroup,
  GraphSection,
  GraphSectionWithPeriodSelector,
  GraphTitle,
  GraphContainer,
  EmptyState,
  PeriodSelectorTopRight,
  PeriodButtonWrapperSmall,
  PeriodButtonSmall,
  PeriodLabelSmall,
} from './styles';
import {
  DateRangeDropdown,
  AthletesDropdown,
  LocationDropdown,
  HillSizeDropdown,
  SessionTypesDropdown,
  GateDropdown,
} from './FilterDropdowns';
import { getAthleteColor } from '../../utils/athleteColors';



interface TrainingSession {
  _id: string;
  date: string;
  location: string;
  hillSize?: string;
  trainingType: string;
  jumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    distance?: number;
    startingGate?: number;
    speed?: number;
    hillSize?: string;
  }>;
  competitionJumps?: Array<{
    athleteId: string | { _id: string; firstName: string; lastName: string };
    firstRoundStartingGate?: number;
    firstRoundDistance?: number;
    firstRoundSpeed?: number;
    secondRoundStartingGate?: number;
    secondRoundDistance?: number;
    secondRoundSpeed?: number;
    thirdRoundStartingGate?: number;
    thirdRoundDistance?: number;
    thirdRoundSpeed?: number;
    fourthRoundStartingGate?: number;
    fourthRoundDistance?: number;
    fourthRoundSpeed?: number;
    fifthRoundStartingGate?: number;
    fifthRoundDistance?: number;
    fifthRoundSpeed?: number;
    hillSize?: string;
  }>;
  groundTrainings?: Array<any>;
  crossCountryTrainings?: Array<any>;
}

interface GateNumberAnalyticsProps {
  isJumperMode?: boolean;
}

const GateNumberAnalytics: React.FC<GateNumberAnalyticsProps> = ({ isJumperMode = false }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const pagePath = getPagePathFromRoute(location.pathname, searchParams);
  
  // Load saved filters on mount
  const savedFilters = loadAnalyticsFilters(pagePath);
  
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [bananaCups, setBananaCups] = useState<any[]>([]);
  const [selectedAthletes, setSelectedAthletes] = useState<string[]>(savedFilters?.selectedAthletes || []);
  const [startDate, setStartDate] = useState<string>(savedFilters?.startDate || '');
  const [endDate, setEndDate] = useState<string>(savedFilters?.endDate || '');
  const [selectedLocation, setSelectedLocation] = useState<string>(savedFilters?.selectedLocation || '');
  const [selectedHillSize, setSelectedHillSize] = useState<string>(savedFilters?.selectedHillSize || '');
  const [selectedGate, setSelectedGate] = useState<string>(savedFilters?.selectedGate || '');
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>(savedFilters?.selectedSessionTypes || []);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<string[]>([]);
  const [hillSizes, setHillSizes] = useState<string[]>([]);
  const [gates, setGates] = useState<number[]>([]);
  const [jumperAthleteId, setJumperAthleteId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(typeof window !== 'undefined' && window.innerWidth <= 768);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Data states for graphs
  const [selectedPeriod, setSelectedPeriod] = useState<'daily' | 'weekly' | 'monthly'>('daily');
  const [avgGateNumberLineDataDaily, setAvgGateNumberLineDataDaily] = useState<any[]>([]);
  const [avgGateNumberLineDataWeekly, setAvgGateNumberLineDataWeekly] = useState<any[]>([]);
  const [avgGateNumberLineDataMonthly, setAvgGateNumberLineDataMonthly] = useState<any[]>([]);
  const [avgDistanceByGateData, setAvgDistanceByGateData] = useState<any[]>([]);
  const [avgSpeedByGateData, setAvgSpeedByGateData] = useState<any[]>([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        
        // If jumper mode, get the logged-in jumper's athlete ID first
        if (isJumperMode) {
          try {
            const myAthlete = await athleteAPI.getMyAthlete();
            const athleteId = myAthlete._id.toString();
            setJumperAthleteId(athleteId);
            setSelectedAthletes([athleteId]);
          } catch (error) {
            console.error('Error loading jumper athlete:', error);
          }
        }
        
        const [athletesData, sessionsResponse, bananaCupsData] = await Promise.all([
          athleteAPI.getAll(),
          trainingSessionAPI.getAll(undefined, undefined, true), // Load all sessions for analytics
          bananaCupAPI.getAll(),
        ]);

        // Handle response - when all=true, returns array directly, otherwise paginated object
        const sessionsData = Array.isArray(sessionsResponse) ? sessionsResponse : (sessionsResponse.sessions || []);

        setAthletes(athletesData);
        setSessions(sessionsData);
        setBananaCups(bananaCupsData || []);

        // Extract unique locations, hill sizes, and gates
        const uniqueLocations = Array.from(new Set(sessionsData.map((s: TrainingSession) => s.location).filter(Boolean)));
        const uniqueHillSizes = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            s.hillSize,
            ...(s.jumps?.map(j => (j as any).hillSize) || []),
            ...(s.competitionJumps?.map(j => (j as any).hillSize) || []),
          ].filter(Boolean))
        ));
        const uniqueGates = Array.from(new Set(
          sessionsData.flatMap((s: TrainingSession) => [
            ...(s.jumps?.map(j => j.startingGate).filter(Boolean) || []),
            ...(s.competitionJumps?.flatMap(j => [
              j.firstRoundStartingGate,
              j.secondRoundStartingGate,
              (j as any).thirdRoundStartingGate,
              (j as any).fourthRoundStartingGate,
              (j as any).fifthRoundStartingGate,
            ].filter(Boolean)) || []),
          ])
        )).sort((a, b) => (a as number) - (b as number));

        setLocations(uniqueLocations as string[]);
        setHillSizes(uniqueHillSizes as string[]);
        setGates(uniqueGates as number[]);

        // Set default date range (last 3 months) only if no saved filters exist
        if (!savedFilters || (!savedFilters.startDate && !savedFilters.endDate)) {
          const today = new Date();
          const threeMonthsAgo = new Date();
          threeMonthsAgo.setMonth(today.getMonth() - 3);
          setEndDate(today.toISOString().split('T')[0]);
          setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [isJumperMode]);

  // Save filters whenever they change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    saveAnalyticsFilters(pagePath, {
      selectedAthletes,
      startDate,
      endDate,
      selectedLocation,
      selectedHillSize,
      selectedSessionTypes,
      selectedGate,
      selectedPeriod,
    });
  }, [pagePath, selectedAthletes, startDate, endDate, selectedLocation, selectedHillSize, selectedSessionTypes, selectedGate, selectedPeriod]);

  // Helper function to format date for input
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    if (dateString.includes('-') && dateString.length === 10) {
      return dateString;
    }
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateString;
  };

  // Helper function to check if date is in range
  const isDateInRange = (dateString: string, start: string, end: string): boolean => {
    if (!start || !end) return true;
    return dateString >= start && dateString <= end;
  };

  // Process gate number data
  useEffect(() => {
    if (!sessions.length || !startDate || !endDate || selectedSessionTypes.length === 0 || selectedAthletes.length === 0) {
      setAvgGateNumberLineDataDaily([]);
      setAvgGateNumberLineDataWeekly([]);
      setAvgGateNumberLineDataMonthly([]);
      setAvgDistanceByGateData([]);
      setAvgSpeedByGateData([]);
      return;
    }

    // Helper function to check if a session has been recorded (has actual data)
    const isRecordedSession = (session: TrainingSession): boolean => {
      const hasJumps = !!(session.jumps && session.jumps.length > 0);
      const hasCompetitionJumps = !!(session.competitionJumps && session.competitionJumps.length > 0);
      const hasGroundTrainings = !!(session.groundTrainings && session.groundTrainings.length > 0);
      const hasCrossCountryTrainings = !!(session.crossCountryTrainings && session.crossCountryTrainings.length > 0);
      return hasJumps || hasCompetitionJumps || hasGroundTrainings || hasCrossCountryTrainings;
    };

    // Filter sessions by date range, location, hill size, gate, and session types
    const filteredSessions = sessions.filter(session => {
      if (!isDateInRange(session.date, startDate, endDate)) return false;
      if (selectedLocation && session.location !== selectedLocation) return false;
      if (!selectedSessionTypes.includes(session.trainingType)) return false;
      if (!isRecordedSession(session)) return false;
      return true;
    });

    // Get athletes to process
    const athletesToProcess = athletes.filter(a => selectedAthletes.includes(a._id));

    // For average gate number over time: athleteId -> date -> gates[]
    const athleteGateMap = new Map<string, Map<string, number[]>>();
    
    // For distance/speed by gate: athleteId -> gate -> {distances[], speeds[]}
    const athleteGateStatsMap = new Map<string, Map<number, { distances: number[]; speeds: number[] }>>();

    filteredSessions.forEach(session => {
      // Process regular jumps
      if (session.jumps) {
        session.jumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;
          if (selectedGate && jump.startingGate !== Number(selectedGate)) return;

          if (jump.startingGate) {
            // Add to gate over time map
            if (!athleteGateMap.has(athleteId)) {
              athleteGateMap.set(athleteId, new Map());
            }
            const dateMap = athleteGateMap.get(athleteId)!;
            if (!dateMap.has(session.date)) {
              dateMap.set(session.date, []);
            }
            dateMap.get(session.date)!.push(jump.startingGate);

            // Add to gate stats map
            if (!athleteGateStatsMap.has(athleteId)) {
              athleteGateStatsMap.set(athleteId, new Map());
            }
            const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
            if (!gateStatsMap.has(jump.startingGate)) {
              gateStatsMap.set(jump.startingGate, { distances: [], speeds: [] });
            }
            const stats = gateStatsMap.get(jump.startingGate)!;
            if (jump.distance) stats.distances.push(jump.distance);
            if (jump.speed) stats.speeds.push(jump.speed);
          }
        });
      }

      // Process competition jumps
      if (session.competitionJumps) {
        session.competitionJumps.forEach(jump => {
          const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
          const athleteId = String(athleteIdRaw);
          
          if (!selectedAthletes.includes(athleteId)) return;
          
          const jumpHillSize = jump.hillSize || session.hillSize;
          if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

          const rounds = [
            { gate: (jump as any).trialRoundStartingGate, distance: (jump as any).trialRoundDistance, speed: (jump as any).trialRoundSpeed },
            { gate: jump.firstRoundStartingGate, distance: jump.firstRoundDistance, speed: jump.firstRoundSpeed },
            { gate: jump.secondRoundStartingGate, distance: jump.secondRoundDistance, speed: jump.secondRoundSpeed },
            { gate: (jump as any).thirdRoundStartingGate, distance: (jump as any).thirdRoundDistance, speed: (jump as any).thirdRoundSpeed },
            { gate: (jump as any).fourthRoundStartingGate, distance: (jump as any).fourthRoundDistance, speed: (jump as any).fourthRoundSpeed },
            { gate: (jump as any).fifthRoundStartingGate, distance: (jump as any).fifthRoundDistance, speed: (jump as any).fifthRoundSpeed },
          ].filter(r => r.gate !== undefined && r.gate !== null);

          rounds.forEach(round => {
            if (selectedGate && round.gate !== Number(selectedGate)) return;

            // Add to gate over time map
            if (!athleteGateMap.has(athleteId)) {
              athleteGateMap.set(athleteId, new Map());
            }
            const dateMap = athleteGateMap.get(athleteId)!;
            if (!dateMap.has(session.date)) {
              dateMap.set(session.date, []);
            }
            dateMap.get(session.date)!.push(round.gate!);

            // Add to gate stats map
            if (!athleteGateStatsMap.has(athleteId)) {
              athleteGateStatsMap.set(athleteId, new Map());
            }
            const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
            if (!gateStatsMap.has(round.gate!)) {
              gateStatsMap.set(round.gate!, { distances: [], speeds: [] });
            }
            const stats = gateStatsMap.get(round.gate!)!;
            if (round.distance) stats.distances.push(round.distance);
            if (round.speed) stats.speeds.push(round.speed);
          });
        });
      }
    });

    // Process banana cup jumps
    if (selectedSessionTypes.includes('banana_cup')) {
      bananaCups.forEach(bananaCup => {
        if (!bananaCup.competitions || !Array.isArray(bananaCup.competitions)) return;
        
        bananaCup.competitions.forEach((competition: any) => {
          if (!competition.isCompleted || !competition.jumps || !Array.isArray(competition.jumps)) return;
          if (!isDateInRange(competition.date, startDate, endDate)) return;
          if (selectedLocation && competition.location !== selectedLocation) return;
          
          competition.jumps.forEach((jump: any) => {
            const athleteIdRaw = typeof jump.athleteId === 'object' ? jump.athleteId._id : jump.athleteId;
            const athleteId = String(athleteIdRaw);
            
            if (!selectedAthletes.includes(athleteId)) return;
            
            const jumpHillSize = jump.hillSize || competition.hillSize;
            if (selectedHillSize && jumpHillSize !== selectedHillSize) return;

            const rounds = [
              { gate: jump.firstRoundStartingGate, distance: jump.firstRoundDistance, speed: jump.firstRoundSpeed },
              { gate: jump.secondRoundStartingGate, distance: jump.secondRoundDistance, speed: jump.secondRoundSpeed },
              { gate: jump.thirdRoundStartingGate, distance: jump.thirdRoundDistance, speed: jump.thirdRoundSpeed },
              { gate: jump.fourthRoundStartingGate, distance: jump.fourthRoundDistance, speed: jump.fourthRoundSpeed },
              { gate: jump.fifthRoundStartingGate, distance: jump.fifthRoundDistance, speed: jump.fifthRoundSpeed },
            ].filter(r => r.gate !== undefined && r.gate !== null);

            rounds.forEach(round => {
              if (selectedGate && round.gate !== Number(selectedGate)) return;

              // Add to gate over time map
              if (!athleteGateMap.has(athleteId)) {
                athleteGateMap.set(athleteId, new Map());
              }
              const dateMap = athleteGateMap.get(athleteId)!;
              if (!dateMap.has(competition.date)) {
                dateMap.set(competition.date, []);
              }
              dateMap.get(competition.date)!.push(round.gate!);

              // Add to gate stats map
              if (!athleteGateStatsMap.has(athleteId)) {
                athleteGateStatsMap.set(athleteId, new Map());
              }
              const gateStatsMap = athleteGateStatsMap.get(athleteId)!;
              if (!gateStatsMap.has(round.gate!)) {
                gateStatsMap.set(round.gate!, { distances: [], speeds: [] });
              }
              const stats = gateStatsMap.get(round.gate!)!;
              if (round.distance) stats.distances.push(round.distance);
              if (round.speed) stats.speeds.push(round.speed);
            });
          });
        });
      });
    }

    // Generate complete date range for average gate number line chart
    const start = new Date(startDate);
    const end = new Date(endDate);
    const allDates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      allDates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }

    // Daily data for average gate number over time
    const avgGateLineDataDaily: any[] = [];
    allDates.forEach(date => {
      const point: any = { date };
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteGateMap.get(athleteIdStr);
        
        if (dateMap && dateMap.has(date)) {
          const gates = dateMap.get(date)!;
          const avg = gates.reduce((a, b) => a + b, 0) / gates.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      avgGateLineDataDaily.push(point);
    });

    // Weekly data for average gate number over time
    const avgGateLineDataWeekly: any[] = [];
    const weekStart = new Date(start);
    weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1); // Start of week (Monday)
    
    while (weekStart <= end) {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const weekKey = weekStart.toISOString().split('T')[0];
      const point: any = { week: weekKey };
      
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteGateMap.get(athleteIdStr);
        
        const weekGates: number[] = [];
        const checkDate = new Date(weekStart);
        while (checkDate <= weekEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            weekGates.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (weekGates.length > 0) {
          const avg = weekGates.reduce((a, b) => a + b, 0) / weekGates.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      avgGateLineDataWeekly.push(point);
      weekStart.setDate(weekStart.getDate() + 7);
    }

    // Monthly data for average gate number over time
    const avgGateLineDataMonthly: any[] = [];
    const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
    
    while (monthStart <= end) {
      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
      const monthLabel = monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      const point: any = { month: monthLabel };
      
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const dateMap = athleteGateMap.get(athleteIdStr);
        
        const monthGates: number[] = [];
        const checkDate = new Date(monthStart);
        while (checkDate <= monthEnd && checkDate <= end) {
          const dateStr = checkDate.toISOString().split('T')[0];
          if (dateMap && dateMap.has(dateStr)) {
            monthGates.push(...dateMap.get(dateStr)!);
          }
          checkDate.setDate(checkDate.getDate() + 1);
        }
        
        if (monthGates.length > 0) {
          const avg = monthGates.reduce((a, b) => a + b, 0) / monthGates.length;
          point[athleteName] = Math.round(avg * 10) / 10;
        } else {
          point[athleteName] = null;
        }
      });
      
      avgGateLineDataMonthly.push(point);
      monthStart.setMonth(monthStart.getMonth() + 1);
    }

    // Process data for distance and speed by gate number
    // Get all unique gates used by selected athletes
    const allGates = new Set<number>();
    athletesToProcess.forEach(athlete => {
      const athleteIdStr = String(athlete._id);
      const gateStatsMap = athleteGateStatsMap.get(athleteIdStr);
      if (gateStatsMap) {
        gateStatsMap.forEach((_, gate) => allGates.add(gate));
      }
    });
    const sortedGates = Array.from(allGates).sort((a, b) => a - b);

    const distanceByGateData: any[] = [];
    const speedByGateData: any[] = [];

    sortedGates.forEach(gate => {
      const distancePoint: any = { gate: `Gate ${gate}` };
      const speedPoint: any = { gate: `Gate ${gate}` };
      
      athletesToProcess.forEach(athlete => {
        const athleteName = `${athlete.firstName} ${athlete.lastName}`;
        const athleteIdStr = String(athlete._id);
        const gateStatsMap = athleteGateStatsMap.get(athleteIdStr);
        
        if (gateStatsMap && gateStatsMap.has(gate)) {
          const stats = gateStatsMap.get(gate)!;
          if (stats.distances.length > 0) {
            const avgDistance = stats.distances.reduce((a, b) => a + b, 0) / stats.distances.length;
            distancePoint[athleteName] = Math.round(avgDistance * 10) / 10;
          } else {
            distancePoint[athleteName] = null;
          }
          if (stats.speeds.length > 0) {
            const avgSpeed = stats.speeds.reduce((a, b) => a + b, 0) / stats.speeds.length;
            speedPoint[athleteName] = Math.round(avgSpeed * 10) / 10;
          } else {
            speedPoint[athleteName] = null;
          }
        } else {
          distancePoint[athleteName] = null;
          speedPoint[athleteName] = null;
        }
      });
      
      distanceByGateData.push(distancePoint);
      speedByGateData.push(speedPoint);
    });

    setAvgGateNumberLineDataDaily(avgGateLineDataDaily);
    setAvgGateNumberLineDataWeekly(avgGateLineDataWeekly);
    setAvgGateNumberLineDataMonthly(avgGateLineDataMonthly);
    setAvgDistanceByGateData(distanceByGateData);
    setAvgSpeedByGateData(speedByGateData);
  }, [sessions, startDate, endDate, selectedAthletes, selectedLocation, selectedHillSize, selectedGate, athletes, bananaCups, selectedSessionTypes]);

  const handleAthleteToggle = (athleteId: string) => {
    setSelectedAthletes(prev =>
      prev.includes(athleteId)
        ? prev.filter(id => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const handleUpdateDate = () => {
    // Data will be re-processed automatically via useEffect
  };

  const handleQuickDateRange = (range: string) => {
    const { startDate: newStartDate, endDate: newEndDate } = getQuickDateRange(range);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleSessionTypeToggle = (sessionType: string) => {
    setSelectedSessionTypes(prev =>
      prev.includes(sessionType)
        ? prev.filter(type => type !== sessionType)
        : [...prev, sessionType]
    );
  };

  const handleAthletesSave = (selected: string[]) => {
    setSelectedAthletes(selected);
  };

  const handleSessionTypesSave = (types: string[]) => {
    setSelectedSessionTypes(types);
  };


  // Helper function to calculate Y-axis domain with padding (1 value lower/higher)
  const calculateYAxisDomainWithPadding = (data: any[], dataKeys: string[]): [number, number] => {
    let minValue = Infinity;
    let maxValue = -Infinity;
    
    data.forEach(point => {
      dataKeys.forEach(key => {
        const value = point[key];
        if (value !== null && value !== undefined && typeof value === 'number') {
          if (value < minValue) {
            minValue = value;
          }
          if (value > maxValue) {
            maxValue = value;
          }
        }
      });
    });
    
    if (minValue === Infinity || maxValue === -Infinity) {
      return [0, 100];
    }
    
    // Round to 1 decimal place for padding
    const padding = 1.0;
    return [Math.round((minValue - padding) * 10) / 10, Math.round((maxValue + padding) * 10) / 10];
  };

  if (isLoading) {
    return (
      <AnalyticsContainer>
        <EmptyState>Loading gate number data...</EmptyState>
      </AnalyticsContainer>
    );
  }

  return (
    <AnalyticsContainer>
      <PageHeader>
        <PageTitle>Gate Number Analytics</PageTitle>
        <PageDescription>
          Analyze gate number usage and performance metrics by gate number for each athlete.
        </PageDescription>
      </PageHeader>

      {/* Data Selection Section */}
      <DataSelectionSection>
        <SelectionGrid>
          <SelectionGroup>
            <DateRangeDropdown
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
              onQuickDateRange={handleQuickDateRange}
              onUpdate={handleUpdateDate}
              formatDateForInput={formatDateForInput}
            />
          </SelectionGroup>

          {!isJumperMode && (
            <SelectionGroup>
              <AthletesDropdown
                athletes={athletes}
                selectedAthletes={selectedAthletes}
                onAthleteToggle={handleAthleteToggle}
                onSave={handleAthletesSave}
              />
            </SelectionGroup>
          )}

          <SelectionGroup>
            <SessionTypesDropdown
              selectedSessionTypes={selectedSessionTypes}
              onSessionTypeToggle={handleSessionTypeToggle}
              onSave={handleSessionTypesSave}
            />
          </SelectionGroup>

          <SelectionGroup>
            <LocationDropdown
              locations={locations}
              selectedLocation={selectedLocation}
              onLocationChange={setSelectedLocation}
            />
          </SelectionGroup>

          <SelectionGroup>
            <HillSizeDropdown
              hillSizes={hillSizes}
              selectedHillSize={selectedHillSize}
              onHillSizeChange={setSelectedHillSize}
            />
          </SelectionGroup>

          <SelectionGroup>
            <GateDropdown
              gates={gates}
              selectedGate={selectedGate}
              onGateChange={setSelectedGate}
            />
          </SelectionGroup>
        </SelectionGrid>
      </DataSelectionSection>

      {/* Graphs Section */}
      
      {/* Average Gate Number Line Chart */}
      <GraphSectionWithPeriodSelector>
        <GraphTitle>{isJumperMode ? 'Average Gate Number' : 'Average Gate Number for Each Athlete'}</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 ? (
          <>
            {(selectedPeriod === 'daily' && avgGateNumberLineDataDaily.length > 0) ||
             (selectedPeriod === 'weekly' && avgGateNumberLineDataWeekly.length > 0) ||
             (selectedPeriod === 'monthly' && avgGateNumberLineDataMonthly.length > 0) ? (
              <>
                <PeriodSelectorTopRight>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'daily'}
                      onClick={() => setSelectedPeriod('daily')}
                    >
                      D
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Daily</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'weekly'}
                      onClick={() => setSelectedPeriod('weekly')}
                    >
                      W
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Weekly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                  <PeriodButtonWrapperSmall>
                    <PeriodButtonSmall
                      $isSelected={selectedPeriod === 'monthly'}
                      onClick={() => setSelectedPeriod('monthly')}
                    >
                      M
                    </PeriodButtonSmall>
                    <PeriodLabelSmall>Monthly</PeriodLabelSmall>
                  </PeriodButtonWrapperSmall>
                </PeriodSelectorTopRight>
                <GraphContainer>
                  <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                    <LineChart data={
                      selectedPeriod === 'daily' ? avgGateNumberLineDataDaily :
                      selectedPeriod === 'weekly' ? avgGateNumberLineDataWeekly :
                      avgGateNumberLineDataMonthly
                    }>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey={selectedPeriod === 'daily' ? 'date' : selectedPeriod === 'weekly' ? 'week' : 'month'} 
                        style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                        tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                        tickFormatter={(value) => {
                          if (selectedPeriod === 'daily') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return value;
                            }
                          } else if (selectedPeriod === 'weekly') {
                            try {
                              const date = new Date(value);
                              if (isNaN(date.getTime())) return value;
                              const weekEnd = new Date(date);
                              weekEnd.setDate(weekEnd.getDate() + 6);
                              return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                            } catch {
                              return value;
                            }
                          } else {
                            return value;
                          }
                        }}
                      />
                <YAxis 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
                    </LineChart>
                  </ResponsiveContainer>
                </GraphContainer>
              </>
            ) : (
              <EmptyState>
                No gate number data available for the selected filters.
              </EmptyState>
            )}
          </>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSectionWithPeriodSelector>

      {/* Average Distance by Gate Number */}
      <GraphSection>
        <GraphTitle>{isJumperMode ? 'Average Distance from Each Gate Number' : 'Average Distance from Each Gate Number for Each Athlete'}</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 && avgDistanceByGateData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={avgDistanceByGateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="gate" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  domain={calculateYAxisDomainWithPadding(
                    avgDistanceByGateData,
                    athletes
                      .filter(a => selectedAthletes.includes(a._id))
                      .map(a => `${a.firstName} ${a.lastName}`)
                  )}
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>

      {/* Average Speed by Gate Number */}
      <GraphSection>
        <GraphTitle>{isJumperMode ? 'Average Inrun Speed from Each Gate Number' : 'Average Inrun Speed from Each Gate Number for Each Athlete'}</GraphTitle>
        {startDate && endDate && selectedSessionTypes.length > 0 && selectedAthletes.length > 0 && avgSpeedByGateData.length > 0 ? (
          <GraphContainer>
            <ResponsiveContainer width="100%" height="100%" minHeight={260}>
              <LineChart data={avgSpeedByGateData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="gate" 
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <YAxis 
                  domain={calculateYAxisDomainWithPadding(
                    avgSpeedByGateData,
                    athletes
                      .filter(a => selectedAthletes.includes(a._id))
                      .map(a => `${a.firstName} ${a.lastName}`)
                  )}
                  style={{ fontSize: '0.7rem', fontFamily: 'Poppins', fontWeight: 900 }}
                  tick={{ style: { fontSize: '0.7rem', fontWeight: 900 } }}
                />
                <Tooltip />
                <Legend wrapperStyle={{ fontFamily: 'Poppins', fontSize: isMobile ? '0.5rem' : '0.75rem' }} iconSize={isMobile ? 8 : 12} />
                {athletes
                  .filter(a => selectedAthletes.includes(a._id))
                  .map((athlete, index) => {
                    const athleteName = `${athlete.firstName} ${athlete.lastName}`;
                    return (
                      <Line
                        key={athlete._id}
                        type="monotone"
                        dataKey={athleteName}
                        stroke={getAthleteColor(athlete, index)}
                        strokeWidth={2}
                        name={athleteName}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                      />
                    );
                  })}
              </LineChart>
            </ResponsiveContainer>
          </GraphContainer>
        ) : (
          <EmptyState>
            {!startDate || !endDate 
              ? 'Please select a date range'
              : selectedSessionTypes.length === 0
              ? 'Please select at least one session type'
              : selectedAthletes.length === 0
              ? 'Please select at least one athlete'
              : 'No data available for the selected filters'}
          </EmptyState>
        )}
      </GraphSection>
    </AnalyticsContainer>
  );
};

export default GateNumberAnalytics;

